import { useRef, useState } from 'react';
import { Download, FileImage, FileText, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import LoyaltyMemberCard, { type LoyaltyCardDesign, type LoyaltyCardTier } from './LoyaltyMemberCard';

export type LoyaltyCardModalProps = {
  open: boolean;
  onClose: () => void;
  design?: LoyaltyCardDesign | string;
  colour?: string;
  logoUrl?: string | null;
  shopName?: string;
  tagline?: string;
  programName?: string;
  customerName?: string;
  memberId?: string;
  points?: number;
  value?: number;
  tier?: LoyaltyCardTier;
  qrDataUrl?: string | null;
  showPoints?: boolean;
  showValue?: boolean;
  showTier?: boolean;
};

async function cardToCanvas(el: HTMLElement) {
  const { default: html2canvas } = await import('html2canvas');
  return html2canvas(el, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    logging: false,
  });
}

export default function LoyaltyCardModal({
  open,
  onClose,
  ...cardProps
}: LoyaltyCardModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<'png' | 'pdf' | null>(null);

  if (!open) return null;

  const fileBase = `loyalty-card-${(cardProps.memberId || cardProps.customerName || 'member')
    .toString()
    .replace(/[^\w.-]+/g, '_')}`;

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy('png');
    try {
      const canvas = await cardToCanvas(cardRef.current);
      const link = document.createElement('a');
      link.download = `${fileBase}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      toast.error('Could not export image');
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async () => {
    if (!cardRef.current) return;
    setBusy('pdf');
    try {
      const canvas = await cardToCanvas(cardRef.current);
      const { jsPDF } = await import('jspdf');
      const img = canvas.toDataURL('image/png');
      // Card aspect ~360x560 — portrait
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [90, 140] });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      pdf.addImage(img, 'PNG', 0, 0, pageW, pageH);
      pdf.save(`${fileBase}.pdf`);
    } catch (e) {
      toast.error('Could not export PDF');
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Loyalty member card"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-white rounded-t-2xl sticky top-0 z-10">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Loyalty card</h3>
            {cardProps.memberId ? (
              <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                QR = {cardProps.memberId} · scan at POS to select
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 flex justify-center bg-slate-100/80">
          <div ref={cardRef}>
            <LoyaltyMemberCard {...cardProps} />
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-2"
            disabled={!!busy}
            onClick={() => void downloadPng()}
          >
            {busy === 'png' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileImage className="w-4 h-4" />
            )}
            Download image
          </button>
          <button
            type="button"
            className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-2"
            disabled={!!busy}
            onClick={() => void downloadPdf()}
          >
            {busy === 'pdf' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Download PDF
          </button>
          <p className="w-full text-[11px] text-slate-400 text-center flex items-center justify-center gap-1">
            <Download className="w-3 h-3" />
            Also available on the customer portal
          </p>
        </div>
      </div>
    </div>
  );
}
